import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Row, Col, Badge } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bugsAPI, projectsAPI, usersAPI } from '../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function BugForm() {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    steps_to_reproduce: '',
    expected_result: '',
    actual_result: '',
    url: '',
    priority: 'Medium',
    bug_type: 'Bug',
    status: 'Open',
    project_id: '',
    assigned_to: '',
    environment: '',
    browser: ''
  });
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(isEditMode);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
    fetchUsers();
    if (isEditMode) {
      fetchBug();
    }
  }, [id]);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getProjects();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  };

  const fetchBug = async () => {
    try {
      setFetchingData(true);
      const response = await bugsAPI.getBug(id);
      const bug = response.data;
      setFormData({
        title: bug.title,
        description: bug.description || '',
        steps_to_reproduce: bug.steps_to_reproduce || '',
        expected_result: bug.expected_result || '',
        actual_result: bug.actual_result || '',
        url: bug.url || '',
        priority: bug.priority,
        bug_type: bug.bug_type || 'Bug',
        status: bug.status,
        project_id: bug.project_id,
        assigned_to: bug.assigned_to || '',
        environment: bug.environment || '',
        browser: bug.browser || ''
      });
    } catch (error) {
      toast.error('Failed to load bug');
      console.error(error);
      navigate('/bugs');
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleEditorChange = (content, field) => {
    setFormData({
      ...formData,
      [field]: content
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const isValid = isImage || isVideo;

      if (!isValid) {
        toast.error(`${file.name} is not a valid image or video file`);
      }
      return isValid;
    });

    setAttachments([...attachments, ...validFiles]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'code-block'],
      ['clean']
    ]
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Bug title is required');
      return;
    }

    if (!formData.description.trim()) {
      toast.error('Bug description is required');
      return;
    }

    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...formData,
        project_id: parseInt(formData.project_id),
        assigned_to: formData.assigned_to ? parseInt(formData.assigned_to) : null,
        environment: formData.environment || null,
        browser: formData.browser || null
      };

      let bugId;
      if (isEditMode) {
        await bugsAPI.updateBug(id, payload);
        bugId = id;
      } else {
        const response = await bugsAPI.createBug(payload);
        bugId = response.data.id;
      }

      // Upload attachments if any
      if (attachments.length > 0) {
        let uploadedCount = 0;
        for (const file of attachments) {
          try {
            await bugsAPI.uploadAttachment(bugId, file);
            uploadedCount++;
          } catch (error) {
            console.error('Failed to upload attachment:', file.name, error);
            toast.warning(`Failed to upload ${file.name}`);
          }
        }
        if (uploadedCount > 0) {
          toast.success(`${isEditMode ? 'Bug updated' : 'Bug created'} successfully with ${uploadedCount} attachment(s)!`);
        }
      } else {
        toast.success(isEditMode ? 'Bug updated successfully!' : 'Bug created successfully!');
      }

      navigate('/bugs');
    } catch (error) {
      toast.error(isEditMode ? 'Failed to update bug' : 'Failed to create bug');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <Container fluid style={{ padding: '2rem 3rem' }}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid style={{ padding: '2rem 3rem', maxWidth: '1400px' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold mb-0" style={{ fontSize: '28px', color: '#1a1a1a' }}>
          {isEditMode ? 'Edit Bug' : 'Create Bug'}
        </h2>
        <Button
          variant="outline-secondary"
          onClick={() => navigate('/bugs')}
          style={{
            borderRadius: '8px',
            padding: '10px 20px',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          Cancel
        </Button>
      </div>

      <Form onSubmit={handleSubmit}>
        <Row className="g-3">
          {/* Left Column - Main Content */}
          <Col lg={8}>
            <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: '10px' }}>
              <Card.Body style={{ padding: '2rem' }}>
                {/* Title */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', fontSize: '14px', color: '#333', marginBottom: '8px' }}>
                    Title <span style={{ color: '#dc3545' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Enter a clear and concise bug title"
                    required
                    style={{
                      borderRadius: '8px',
                      padding: '12px 16px',
                      fontSize: '15px',
                      border: '1px solid #e0e0e0'
                    }}
                  />
                </Form.Group>

                {/* Description */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', fontSize: '14px', color: '#333', marginBottom: '8px' }}>
                    Description <span style={{ color: '#dc3545' }}>*</span>
                  </Form.Label>
                  <div style={{ borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                    <ReactQuill
                      theme="snow"
                      value={formData.description}
                      onChange={(content) => handleEditorChange(content, 'description')}
                      modules={quillModules}
                      placeholder="Describe the bug in detail..."
                      style={{ minHeight: '180px' }}
                    />
                  </div>
                </Form.Group>

                {/* Steps to Reproduce */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', fontSize: '14px', color: '#333', marginBottom: '8px' }}>
                    Steps to Reproduce
                  </Form.Label>
                  <div style={{ borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                    <ReactQuill
                      theme="snow"
                      value={formData.steps_to_reproduce}
                      onChange={(content) => handleEditorChange(content, 'steps_to_reproduce')}
                      modules={quillModules}
                      placeholder="1. Go to...\n2. Click on...\n3. Observe..."
                      style={{ minHeight: '150px' }}
                    />
                  </div>
                </Form.Group>

                {/* Expected & Actual Results */}
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label style={{ fontWeight: '600', fontSize: '14px', color: '#333', marginBottom: '8px' }}>
                        Expected Result
                      </Form.Label>
                      <div style={{ borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                        <ReactQuill
                          theme="snow"
                          value={formData.expected_result}
                          onChange={(content) => handleEditorChange(content, 'expected_result')}
                          modules={quillModules}
                          placeholder="What should happen?"
                          style={{ minHeight: '120px' }}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label style={{ fontWeight: '600', fontSize: '14px', color: '#333', marginBottom: '8px' }}>
                        Actual Result
                      </Form.Label>
                      <div style={{ borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                        <ReactQuill
                          theme="snow"
                          value={formData.actual_result}
                          onChange={(content) => handleEditorChange(content, 'actual_result')}
                          modules={quillModules}
                          placeholder="What actually happens?"
                          style={{ minHeight: '120px' }}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* URL */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', fontSize: '14px', color: '#333', marginBottom: '8px' }}>
                    URL
                  </Form.Label>
                  <Form.Control
                    type="url"
                    name="url"
                    value={formData.url}
                    onChange={handleChange}
                    placeholder="https://example.com/page-where-bug-occurs"
                    style={{
                      borderRadius: '8px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      border: '1px solid #e0e0e0'
                    }}
                  />
                </Form.Group>

                {/* Attachments */}
                <Form.Group>
                  <Form.Label style={{ fontWeight: '600', fontSize: '14px', color: '#333', marginBottom: '8px' }}>
                    Attachments (Images/Videos)
                  </Form.Label>
                  <div
                    style={{
                      border: '2px dashed #e0e0e0',
                      borderRadius: '8px',
                      padding: '2rem',
                      textAlign: 'center',
                      backgroundColor: '#fafafa',
                      cursor: 'pointer'
                    }}
                    onClick={() => document.getElementById('fileInput').click()}
                  >
                    <i className="bi bi-cloud-upload" style={{ fontSize: '32px', color: '#999', marginBottom: '8px', display: 'block' }}></i>
                    <p style={{ color: '#666', marginBottom: '4px', fontSize: '14px' }}>
                      Click to upload or drag and drop
                    </p>
                    <p style={{ color: '#999', fontSize: '12px', marginBottom: 0 }}>
                      PNG, JPG, GIF, MP4, MOV (max 10MB)
                    </p>
                    <input
                      id="fileInput"
                      type="file"
                      multiple
                      accept="image/*,video/*"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                  </div>

                  {attachments.length > 0 && (
                    <div className="mt-3">
                      <Row className="g-2">
                        {attachments.map((file, index) => (
                          <Col key={index} xs={6} md={4}>
                            <div style={{
                              position: 'relative',
                              borderRadius: '8px',
                              border: '1px solid #e0e0e0',
                              padding: '8px',
                              backgroundColor: 'white'
                            }}>
                              <div className="d-flex align-items-center">
                                <i className={`bi ${file.type.startsWith('image/') ? 'bi-file-image' : 'bi-file-play'}`} style={{ fontSize: '24px', color: '#0066cc', marginRight: '8px' }}></i>
                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                  <p style={{ fontSize: '12px', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {file.name}
                                  </p>
                                  <p style={{ fontSize: '11px', color: '#999', margin: 0 }}>
                                    {(file.size / 1024).toFixed(1)} KB
                                  </p>
                                </div>
                                <Button
                                  variant="link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeAttachment(index);
                                  }}
                                  style={{ padding: 0, marginLeft: '8px', color: '#dc3545' }}
                                >
                                  <i className="bi bi-x-circle"></i>
                                </Button>
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Column - Metadata */}
          <Col lg={4}>
            <Card className="border-0 shadow-sm" style={{ borderRadius: '10px', position: 'sticky', top: '20px' }}>
              <Card.Body style={{ padding: '1.5rem' }}>
                <h6 className="fw-bold mb-3" style={{ fontSize: '15px', color: '#1a1a1a' }}>Bug Details</h6>

                {/* Project */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', fontSize: '13px', color: '#666', marginBottom: '6px' }}>
                    Project <span style={{ color: '#dc3545' }}>*</span>
                  </Form.Label>
                  <Form.Select
                    name="project_id"
                    value={formData.project_id}
                    onChange={handleChange}
                    required
                    style={{
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <option value="">Select project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* Priority */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', fontSize: '13px', color: '#666', marginBottom: '6px' }}>
                    Priority
                  </Form.Label>
                  <Form.Select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    style={{
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <option value="Low">🟢 Low</option>
                    <option value="Medium">🟡 Medium</option>
                    <option value="High">🟠 High</option>
                    <option value="Critical">🔴 Critical</option>
                  </Form.Select>
                </Form.Group>

                {/* Bug Type */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', fontSize: '13px', color: '#666', marginBottom: '6px' }}>
                    Type
                  </Form.Label>
                  <Form.Select
                    name="bug_type"
                    value={formData.bug_type}
                    onChange={handleChange}
                    style={{
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <option value="Bug">Bug</option>
                    <option value="Feature">Feature</option>
                    <option value="Enhancement">Enhancement</option>
                    <option value="Ticket">Ticket</option>
                  </Form.Select>
                </Form.Group>

                {/* Environment */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', fontSize: '13px', color: '#666', marginBottom: '6px' }}>
                    Environment
                  </Form.Label>
                  <Form.Select
                    name="environment"
                    value={formData.environment}
                    onChange={handleChange}
                    style={{
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <option value="">Select environment</option>
                    <option value="Production">Production</option>
                    <option value="Staging">Staging</option>
                    <option value="UAT">UAT</option>
                  </Form.Select>
                </Form.Group>

                {/* Browser */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', fontSize: '13px', color: '#666', marginBottom: '6px' }}>
                    Browser
                  </Form.Label>
                  <Form.Select
                    name="browser"
                    value={formData.browser}
                    onChange={handleChange}
                    style={{
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <option value="">Select browser</option>
                    <option value="Web">Web</option>
                    <option value="Mobile">Mobile</option>
                    <option value="App">App</option>
                  </Form.Select>
                </Form.Group>

                {/* Status - Only show in edit mode */}
                {isEditMode && (
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: '600', fontSize: '13px', color: '#666', marginBottom: '6px' }}>
                      Status
                    </Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      style={{
                        borderRadius: '8px',
                        padding: '10px 12px',
                        fontSize: '14px',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <option value="Open">Open</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Resolved">Resolved</option>
                      <option value="Closed">Closed</option>
                      <option value="Re-opened">Re-opened</option>
                      <option value="Not a Bug">Not a Bug</option>
                      <option value="Cannot Reproduce">Cannot Reproduce</option>
                      <option value="Pending QA">Pending QA</option>
                    </Form.Select>
                  </Form.Group>
                )}

                {/* Assign To */}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', fontSize: '13px', color: '#666', marginBottom: '6px' }}>
                    Assign To
                  </Form.Label>
                  <Form.Select
                    name="assigned_to"
                    value={formData.assigned_to}
                    onChange={handleChange}
                    style={{
                      borderRadius: '8px',
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <option value="">Unassigned</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name} ({user.role})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <hr style={{ margin: '1.5rem 0', borderColor: '#e0e0e0' }} />

                {/* Action Buttons */}
                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading}
                    style={{
                      borderRadius: '8px',
                      padding: '12px',
                      fontWeight: '600',
                      fontSize: '14px',
                      border: 'none'
                    }}
                  >
                    {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Bug' : 'Create Bug')}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/bugs')}
                    disabled={loading}
                    style={{
                      borderRadius: '8px',
                      padding: '12px',
                      fontWeight: '500',
                      fontSize: '14px'
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Form>
    </Container>
  );
}

export default BugForm;
